/**
 * Formatage — centralisé ici parce qu'un prix affiché « 65.00€ » sur une
 * surface et « 65 € » sur une autre suffit à faire perdre le sentiment de soin.
 *
 * Toutes les fonctions sont pures et prennent un fuseau explicite : le salon
 * vit dans SA timezone, jamais dans celle du téléphone de la cliente en voyage.
 */

const LOCALE = "fr-FR";

/* -------------------------------------------------------------------------- */
/*  Argent                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Centimes → « 65 € » ou « 64,50 € ».
 * Les décimales n'apparaissent que si elles existent : « 65,00 € » alourdit
 * une grille tarifaire pour rien.
 */
export function formatPrice(cents: number, options?: { from?: boolean }): string {
  const hasCents = cents % 100 !== 0;
  const formatted = new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(cents / 100);

  // « dès 65 € » : promesse honnête quand le tarif dépend de la longueur.
  return options?.from ? `dès ${formatted}` : formatted;
}

/* -------------------------------------------------------------------------- */
/*  Durée                                                                      */
/* -------------------------------------------------------------------------- */

/** Minutes → « 45 min », « 1 h », « 1 h 30 ». Jamais « 90 min ». */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, "0")}`;
}

/* -------------------------------------------------------------------------- */
/*  Temps                                                                      */
/* -------------------------------------------------------------------------- */

export function formatTime(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** « 14:00 – 15:30 » : tiret demi-cadratin entouré d'espaces insécables fines. */
export function formatTimeRange(start: Date, end: Date, timeZone: string): string {
  return `${formatTime(start, timeZone)} – ${formatTime(end, timeZone)}`;
}

/** Clé calendaire `2026-09-01` dans le fuseau du salon (et non celui d'UTC). */
export function dayKey(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return parts;
}

/**
 * Libellé de jour orienté usage : « Aujourd'hui » et « Demain » portent plus
 * d'information qu'une date, et c'est ce que la cliente cherche des yeux.
 */
export function formatDayLabel(date: Date, timeZone: string, now = new Date()): string {
  const target = dayKey(date, timeZone);
  const today = dayKey(now, timeZone);
  const tomorrow = dayKey(new Date(now.getTime() + 86_400_000), timeZone);

  if (target === today) return "Aujourd'hui";
  if (target === tomorrow) return "Demain";

  const label = new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** « Jeudi 14:00 » — l'accroche compacte des 3 prochaines dispos. */
export function formatSlotLabel(date: Date, timeZone: string, now = new Date()): string {
  return `${formatDayLabel(date, timeZone, now)} ${formatTime(date, timeZone)}`;
}

/**
 * Âge d'un message pour la liste d'inbox. Court par construction : la colonne
 * fait 40px, « il y a environ 3 heures » n'y tient pas et n'apporte rien.
 */
export function formatMessageAge(date: Date, now = new Date()): string {
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "hier";
  if (days < 7) return `${days} j`;
  return new Intl.DateTimeFormat(LOCALE, { day: "numeric", month: "short" }).format(date);
}

/* -------------------------------------------------------------------------- */
/*  Personnes                                                                  */
/* -------------------------------------------------------------------------- */

/** Initiales pour l'avatar de repli. Une seule lettre si pas de nom. */
export function initials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.trim().charAt(0) ?? "";
  const last = lastName?.trim().charAt(0) ?? "";
  return (first + last).toUpperCase() || "?";
}

/** « Léa D. » — on protège le nom complet, y compris sur un écran d'accueil. */
export function shortName(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.trim() ?? "";
  const last = lastName?.trim();
  return last ? `${first} ${last.charAt(0).toUpperCase()}.` : first;
}
